import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { useSession } from "../auth/client";

interface UserData {
  _id?: string;
  email: string;
  name: string;
  role: "admin" | "member";
  permissions: string[];
  status: "active" | "pending";
  avatarColor?: string;
}

interface UserContextType {
  user: UserData | null;
  loading: boolean;
  error: string | null;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  refetch: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const { data: session, isPending } = useSession();
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchUser = useCallback(async () => {
    if (!session?.user) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch("/api/me");
      const result = await response.json();

      if (result.success) {
        setUser(result.data);
      } else {
        setError(result.error || "Failed to fetch user data");
        setUser(null);
      }
    } catch (err) {
      console.error("Error fetching user:", err);
      setError("Failed to fetch user data");
      setUser(null);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [session?.user?.email]); // Only depend on email, not the entire user object

  useEffect(() => {
    // Only fetch if we haven't fetched yet, or if the user email changed (different user logged in)
    if (!hasFetched || (session?.user?.email && !user)) {
      fetchUser();
    }
  }, [fetchUser, hasFetched, session?.user?.email, user]);

  // Refetch permissions when the user returns to the tab (picks up admin permission changes)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && session?.user?.email) {
        fetchUser();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [fetchUser, session?.user?.email]);

  const hasPermission = useCallback((permission: string): boolean => {
    if (!user) return false;
    // Admins have access to everything
    if (user.role === "admin") return true;
    return user.permissions.includes(permission);
  }, [user]);

  const hasAnyPermission = useCallback((permissions: string[]): boolean => {
    if (!user) return false;
    // Admins have access to everything
    if (user.role === "admin") return true;
    return permissions.some(p => user.permissions.includes(p));
  }, [user]);

  return (
    <UserContext.Provider value={{
      user,
      loading,
      error,
      hasPermission,
      hasAnyPermission,
      refetch: fetchUser,
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
